import { data as routerData } from 'react-router';
import type { LoaderFunctionArgs } from '~/types/route-args';
import { useEffect, lazy, Suspense, useState } from 'react';
import { useHydrated } from 'remix-utils';
import { useLoaderData, useLocation } from 'react-router';
import { useInView } from 'react-intersection-observer';
import { getPreloadImageLink } from '~/modules/images';
import {
    mergeMeta,
    createMetaTitle,
    createMetaDescription,
    fallbackOGSocialImage,
    createOGTags,
    getCanonicalHreflang,
} from '@modules/seo';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import { createVideoSchema } from '~/modules/seo/schema/video-schema';
import { createProjectSchema } from '~/modules/seo/schema/project-schema';
import { createLDSchema } from '~/modules/seo/util/create-ld-schema';
import Container from '@modules/project/component/container/Container';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import makeProject from '@modules/project/util/make-project';
import getProjectQuery from '@modules/project/util/get-project-query';
import getFeaturedProductsQuery from '@modules/project/util/get-featured-products-query';
import getFeaturedProfessionalsQuery from '@modules/project/util/get-featured-professionals-query';
import getFeaturedProjectsQuery from '@modules/project/util/get-featured-projects-query';
import { getPopularProjects } from '~/modules-rocco/projects/api/get-popular-projects.server';
import makeFeaturedProducts from '@modules/project/util/make-featured-products.server';
import makeFeaturedProjects from '@modules/project/util/make-featured-projects';
import makeFeaturedProfessionals from '@modules/project/util/make-featured-professionals';
import DetailModules from '~/modules/page-builder/component/detail/detail-modules/DetailModules';
import { getBMPagePath } from '~/modules/root/util/get-bm-page-path';
import { ProductTaggingContextProvider } from '~/modules/product-tagging/context';
import { getPermissions, useEditPermissions } from '~/modules/user';
import { PermissionEnum } from 'generated/graphql';
import getMemberRecentlyTagged from '@modules/product-tagging/api/get-member-recently-tagged.server';
import { useCurrentCategory } from '@rocco/ui/website-header/hooks/use-current-category';
import { getPageLocalisation } from '~/modules/seo/api/get-page-localisation.server';
import { PaintPickerContextProvider as PaintPickerContextProviderV2 } from '@modules/paint-picker-new/context/PaintPickerContext';
import { unsafeVariant_DO_NOT_USE } from '~/utils/unsafeVariant';
import { useRefreshOnRotate } from '@modules/root/util/use-refresh-on-rotate';
import invariant from 'tiny-invariant';
import getProjectBuilderModulesQuery from '@modules/project/util/get-project-builder-modules.query';
import { useSetEditMode } from '@modules/root/hook/use-set-edit-mode';
import { getPrimaryCTA } from '@modules/professional/util/professional-cta-helper';
import { PrimaryCtaEnum } from 'generated/graphql';
import { adsRequest } from '~/modules/adverts/api/ads-request.server';
import getModuleWithAds from '@modules/project/util/get-module-with-ads';
import { useMemo, useRef } from 'react';
import { createWarningMessages } from '@modules/root/hook/use-warning-messages';
import { useLocationMemo } from '@modules/adverts/hook/use-location-memo';
import { getProjectPlacements } from '~/modules/adverts/utils/project-placement-helper.server';
import { makeProjectItemData } from '~/modules/adverts/utils/get-item-data.server';
import { noIndexCrossCountry } from '~/modules/seo/util/no-index-cross-country';
import { getRouteData } from '~/modules/root/util/get-route-data';
import type { APHandle } from '~/modules/root/type/handle';
import { useProjectPreviewRouteData } from '@modules/project/hook/use-project-preview-route-data';
import { getSession } from '~/modules/root/util/session.server';
import dayjs from 'dayjs';
import { ModulesContextProvider } from '~/modules/page-builder/context/modules-context';
import type { PageBuilderModuleType } from '@modules/page-builder/types';
import type { ProjectAd } from '@modules/adverts/type';
import ProjectPageHeader from '~/modules/project/component/page-header/ProjectPageHeader';
import { useSetAtom } from 'jotai';
import { setCloneCountryStateAtom } from '~/state/ui-state-clone-country-link';
import { getProjectTeamMembers } from '~/modules/professional/api/get-project-team-members.server';
import { useOpenLogin } from '~/utils-rocco/hooks/open-login-modal';
import { splitContentByChars } from '~/modules/project/util/split-content';
import { getDirectoryItemByIDs } from '~/modules/directory/api/get-directory-items-admin.server';
import { getDirectoryFeaturedProjects } from '~/modules/directory/api/get-directory-featured-project.server';
import ProjectPageContent from '~/modules/project/component/content/ProjectPageContent';

import { ProjectDesktopWebsiteCTA } from '~/modules/project/component/website-cta/ProjectDesktopWebsiteCTA';

/**
 * Lazy-loaded components for below-fold mobile content.
 * These reduce initial bundle size and defer rendering until needed.
 */

const PageBottomSection = lazy(() =>
    import('~/ui-rocco/pages/shared/PageBottomSection').then((mod) => ({
        default: mod.PageBottomSection,
    })),
);

const ProjectSharesAndTags = lazy(() =>
    import('~/modules/project/component/details-accordion/ProjectSharesAndTags').then(
        (mod) => ({ default: mod.ProjectSharesAndTags }),
    ),
);

/**
 * SSR-aware lazy section that defers rendering of below-fold content.
 *
 * Behavior:
 * - SSR: Always renders children (for SEO crawlers)
 * - Client after hydration: Uses IntersectionObserver to lazy-load content
 *
 * This ensures crawlers see full content while users benefit from lazy loading.
 *
 * Note: We delay switching to "lazy mode" until the observer has had a chance
 * to fire, preventing a flash where content briefly shows skeleton after hydration.
 */
const LazySection = ({
    children,
    fallback,
    rootMargin = '200px',
    className,
}: {
    children: React.ReactNode;
    fallback?: React.ReactNode;
    rootMargin?: string;
    className?: string;
}) => {
    const isHydrated = useHydrated();
    // Track when hydration has "settled" (observer had time to fire)
    const [hydrationSettled, setHydrationSettled] = useState(false);

    const { ref, inView } = useInView({
        rootMargin,
        triggerOnce: true,
        skip: !isHydrated, // Skip observation during SSR
    });

    useEffect(() => {
        if (isHydrated && !hydrationSettled) {
            // Give IntersectionObserver time to detect initial visibility
            // before switching to lazy mode. This prevents the flash where
            // content briefly shows skeleton right after hydration.
            const timer = requestAnimationFrame(() => {
                setHydrationSettled(true);
            });
            return () => cancelAnimationFrame(timer);
        }
    }, [isHydrated, hydrationSettled]);

    // SSR: always render children for SEO
    // Hydration not settled: keep rendering to prevent flash
    // Hydration settled: render based on visibility
    const shouldRender = !isHydrated || !hydrationSettled || inView;

    return (
        <div ref={ref} className={className}>
            {shouldRender ? children : fallback}
        </div>
    );
};

/** Skeleton placeholder for below-fold content sections */
const BelowFoldSkeleton = () => (
    <div className="flex flex-col gap-8 px-4">
        <div className="h-32 animate-pulse rounded-lg bg-gray-100" />
        <div className="h-48 animate-pulse rounded-lg bg-gray-100" />
        <div className="h-40 animate-pulse rounded-lg bg-gray-100" />
    </div>
);

const ProjectSharesSkeleton = () => (
    <div className="mb-8 h-48 animate-pulse rounded-2xl bg-gray-100 md:mb-12" />
);

export const meta = mergeMeta<typeof loader>(
    () => {
        return [];
    },
    (props) => {
        const { data, matches } = props;

        const { rootData } = getRouteData(matches);

        const project = data?.project;
        const projectTitle =
            project?.Title &&
            project?.Professional?.Title &&
            `${project?.Title} by ${project?.Professional?.Title}`;

        const title = createMetaTitle(props, [
            projectTitle,
            data?.localisation?.Title,
        ]);

        const description = createMetaDescription([
            data?.localisation?.Description,
            data?.project?.Content,
        ]);

        const fallbackSocialImage = fallbackOGSocialImage(
            props,
            data?.project?.BannerImages?.[0]?.LibraryImageFile
                ?.DetailBannerStandard?.Desktop,
        );

        const videoSchemas =
            project?.Modules.filter(
                (module) =>
                    module.presetType === 'SingleVideoModule' ||
                    module.presetType === 'VideoParagraphModule',
            )
                .flatMap((module) => module.columns)
                .map((column) => {
                    const { video } = column;
                    // take project's PublishDate if we don't have any video dateCreated
                    const publishDate =
                        video?.dateCreated ??
                        (data?.project?.PublishDate
                            ? dayjs(
                                  data.project.PublishDate,
                                  'D-MM-YY',
                              ).format()
                            : ' ');

                    return createVideoSchema({
                        name: video?.title ?? data?.project?.Title ?? '',
                        publishDate: publishDate,
                        professionalName: project.Professional.Title ?? '',
                        url: video?.videoURL ?? '',
                        thumbnailUrl: video?.coverImageThumbnail ?? '',
                        description: video?.title || description,
                    });
                }) ?? [];

        // CreativeWork JSON-LD for the project itself (the professional is the creator, the gallery the
        // images), so the largest indexable surface is eligible for richer results and Google Images.
        const baseUrl = rootData ? `https://${rootData.env.host}` : '';
        // FacebookPreviewImage is stored site-relative, so make it absolute for JSON-LD `image`
        // (mirrors professional-schema). fallbackSocialImage is already absolute, so leave it as-is.
        const projectImage = data?.project?.FacebookPreviewImage
            ? baseUrl + data.project.FacebookPreviewImage
            : fallbackSocialImage;
        const projectPublishDate = data?.project?.PublishDate
            ? dayjs(data.project.PublishDate, 'D-MM-YY').format()
            : undefined;
        const projectLocation = [
            project?.Location?.Suburb,
            project?.Location?.Region,
            project?.Location?.Country,
        ]
            .filter(Boolean)
            .join(', ');

        const projectSchema = project
            ? createProjectSchema({
                  url: baseUrl + props.location.pathname,
                  name: project.Title ?? '',
                  description,
                  images: projectImage ? [projectImage] : [],
                  professionalName: project.Professional?.Title ?? '',
                  datePublished: projectPublishDate,
                  category: project.Categories?.[0]?.PrettyTitle ?? undefined,
                  location: projectLocation || undefined,
              })
            : [];

        const ldSchema = createLDSchema([...videoSchemas, projectSchema]);

        const bannerImage = data?.project?.Banner?.imageUrl;

        const preloadImages =
            data?.deviceType === 'mobile'
                ? getPreloadImageLink(
                      undefined,
                      bannerImage,
                      '100vw', // Mobile banner is full viewport width
                      { mobileWidth: 414 },
                  )
                : getPreloadImageLink(
                      bannerImage,
                      undefined,
                      getImagesSizes(1820),
                      { desktopWidth: 1820 },
                  );

        return [
            { title },
            { name: 'description', content: description },
            ...createOGTags({
                title,
                description,
                meta: props,
                facebookSocialImage:
                    data?.project?.FacebookPreviewImage ?? fallbackSocialImage,
                type: 'website',
            }),
            ...getCanonicalHreflang(props, {
                professionalCountry: data?.project?.Professional?.Country,
            }),
            ...ldSchema,
            ...preloadImages,
            ...noIndexCrossCountry(
                data?.project?.Professional?.Country,
                rootData?.localisation?.country,
                data?.localisation,
            ),
        ];
    },
);

export const handle: APHandle = {
    fadeTopNavAfterPosition: 200,
    hideContactArchiPro: true,
};

export const loader = async (args: LoaderFunctionArgs) => {
    const { params, context, request } = args;
    invariant(!!params.urlSegment, 'A project URL segment is required');
    const { appDisplayMode, user } = context;
    const isMobile = appDisplayMode === 'mobile';

    const projectPromise = getProjectQuery(args, false, false);
    const [
        projectResult,
        featuredProductsResult,
        featuredProfessionalsResult,
        featuredProjectsResult,
        popularProjectsResult,
        memberRecentlyTagged,
        localisation,
        projectBuilderModulesResult,
        directoryFeaturedResult,
        projectAds,
    ] = await Promise.all([
        projectPromise,
        getFeaturedProductsQuery(args),
        getFeaturedProfessionalsQuery(args),
        getFeaturedProjectsQuery(args),
        getPopularProjects({ limit: 10 }, context),
        getMemberRecentlyTagged(args),
        getPageLocalisation(args),
        getProjectBuilderModulesQuery(args, true, false),
        getDirectoryFeaturedProjects(context),
        (async () => {
            // waterfall request to get the project data and feed it to the Ads API.
            // Avoid duplicated heavy ORM queries for the project data in the Ads BE.
            const projectResponse = await projectPromise;
            unsafeVariant_DO_NOT_USE(projectResponse);

            const projectItemData = await makeProjectItemData(
                args,
                projectResponse,
            );

            return adsRequest({
                type: 'ProjectPlacementGroup',
                requestUrl: request.url,
                item: projectItemData,
                ...args.context,
            });
        })(),
    ]);

    unsafeVariant_DO_NOT_USE(featuredProductsResult);
    unsafeVariant_DO_NOT_USE(featuredProfessionalsResult);
    unsafeVariant_DO_NOT_USE(featuredProjectsResult);
    unsafeVariant_DO_NOT_USE(projectResult);

    invariant(
        !!projectBuilderModulesResult,
        'Project builder modules not found',
    );

    const project = await makeProject(
        args,
        projectResult,
        projectBuilderModulesResult,
        user,
    );

    // Determine if project has a valid banner (image or video)
    const hasBanner = !!(
        project.Banner?.imageUrl || project.Banner?.videoBannerUrl
    );

    const isPinnedToDirectory =
        directoryFeaturedResult &&
        'directoryFeaturedProjects' in directoryFeaturedResult
            ? directoryFeaturedResult.directoryFeaturedProjects.some(
                  (fp) => fp.ProjectID === project.ID,
              )
            : false;

    const { firstCol, secondCol, isOverflowing } = splitContentByChars(
        project.Content ?? '',
    );

    // Await all remaining data in parallel for SEO optimization
    const [
        featuredProducts,
        featuredProfessionals,
        featuredProjects,
        projectTeamMembers,
        projectKPI,
    ] = await Promise.all([
        makeFeaturedProducts(args, featuredProductsResult, project.Title, user),
        makeFeaturedProfessionals(args, featuredProfessionalsResult, project),
        makeFeaturedProjects(args, featuredProjectsResult, project),
        (async () => {
            const teamMembers = await getProjectTeamMembers({
                projectId: String(project.ID),
                ...args.context,
            });
            return teamMembers ?? [];
        })(),
        getDirectoryItemByIDs({
            IDs: [project.ID],
            ...args.context,
        }),
    ]);

    const primaryCTA = getPrimaryCTA({
        primaryCTA: PrimaryCtaEnum.AutoOptimise,
        pageType: 'project',
    });

    const showPageNotLiveWarning = getPermissions(
        user,
        PermissionEnum.BmStaffView,
        project.Professional.ID,
    );

    // Create warning message for the WarningBanner
    const warningMessages = createWarningMessages(
        project.Professional?.Country || 'NZ',
        context.localisation.country,
        !project.IsLive || project.Disable || project.Professional?.Disable,
        'project',
        false,
        {
            showPageNotLiveWarning: showPageNotLiveWarning,
            isAPStaff: getPermissions(user, PermissionEnum.BmStaffView),
        },
    );

    const ads = projectAds ? getProjectPlacements(projectAds, isMobile) : [];

    const session = await getSession(request);

    return routerData(
        {
            project,
            deviceType: context.device.type,
            projectKPI,
            summary: {
                firstCol,
                secondCol,
                isOverflowing,
            },
            featuredProducts,
            featuredProfessionals,
            featuredProjects,
            popularProjects: popularProjectsResult,
            memberRecentlyTagged,
            moduleMeta: project.meta,
            memberPermissions: {
                canCreateTag: getPermissions(
                    user,
                    PermissionEnum.BmProductTaggingCreate,
                    project.Professional.ID,
                ),
                canDeleteTag: getPermissions(
                    user,
                    PermissionEnum.BmProductTaggingDelete,
                    project.Professional.ID,
                ),
                hasMarketingView: getPermissions(
                    user,
                    PermissionEnum.BmMarketingView,
                    project.Professional.ID,
                ),
            },
            localisation,
            primaryCTA,
            ads,
            warningMessages,
            projectTeamMembers,
            isPinnedToDirectory,
            pageTheme: hasBanner ? 'transparent' : 'default',
        },
        {
            headers: await session.getHeaders(),
        },
    );
};

/**
 * Renders page builder modules with advertisements.
 * On mobile, splits modules into initial (above-fold) and deferred (below-fold) sets
 * to improve initial page load performance.
 */
const ModuleAds = ({
    modules,
    ads,
    isMobile,
}: {
    modules: PageBuilderModuleType[];
    ads: ProjectAd[];
    isMobile: boolean;
}) => {
    const location = useLocation();
    const hideAd = location.state?.hideAd;

    // Keep the first value returned by loader to avoid changing when loader revalidates
    const adsMemoized = useLocationMemo(
        hideAd || typeof document === 'undefined' ? [] : ads,
    );
    const modulesWithAds = useMemo(
        () => getModuleWithAds(modules, adsMemoized),
        [modules, adsMemoized],
    );

    // Render all modules - LazyImageModule handles individual lazy loading
    // with content-visibility:auto and IntersectionObserver per module
    return <DetailModules isMobile={isMobile} modules={modulesWithAds} />;
};

const ProjectPage = () => {
    const { desktop: isDesktop, mobile: isMobile } = useAppDisplayModeContext();
    useRefreshOnRotate();
    const { memberRecentlyTagged, memberPermissions, ads, moduleMeta } =
        useLoaderData<typeof loader>();

    const project = useProjectPreviewRouteData();

    const { setCategory } = useCurrentCategory();

    const setCountryData = useSetAtom(setCloneCountryStateAtom);

    useEffect(() => {
        setCountryData({
            urlSegment: project?.URLSegment,
            currentCountry: project?.Professional?.Country,
            pageType: 'project',
            associatedPages: project?.AssociatedPages.edges.length
                ? project?.AssociatedPages
                : project?.Professional?.ProfessionalAssociatedPages,
        });
    }, [project, setCountryData]);

    // Set the current category for header navigation
    useEffect(() => {
        setCategory('Projects');
        return () => setCategory(null);
    }, [setCategory]);

    const { canCreateTag, canDeleteTag, hasMarketingView } = memberPermissions;
    const canDownloadBannerImage = !isMobile && hasMarketingView;
    const { canEditProjects } = useEditPermissions(project.Professional.ID);
    const projectEditPath = getBMPagePath(
        project.Professional.URLSegment,
        'projects',
        project.URLSegment,
    );
    useSetEditMode(projectEditPath, canEditProjects);

    const PickerContext = PaintPickerContextProviderV2;

    const modulesContainerRef = useRef<HTMLDivElement>(null);
    const openLogin = useOpenLogin();

    const projectModules = (
        <ProductTaggingContextProvider
            canCreateTag={isDesktop && canCreateTag}
            canDeleteTag={canDeleteTag}
            recentlyTaggedProducts={memberRecentlyTagged.products}
            recentlyTaggedProfessionals={memberRecentlyTagged.professionals}
        >
            <ModulesContextProvider meta={moduleMeta}>
                {isDesktop && (
                    <>
                        <div className="mx-12 mb-2 font-display text-6.5 font-normal leading-[130%] text-[#141414]">
                            Project Gallery
                        </div>
                        <div className="relative mx-12 border-t border-[#141414]" />
                    </>
                )}
                <div
                    ref={modulesContainerRef}
                    id="project-gallery"
                    className="mt-11.5 -scroll-mt-32 md:mb-24 md:mt-11.5"
                >
                    <ModuleAds
                        isMobile={isMobile}
                        modules={project.Modules}
                        ads={ads}
                    />
                </div>
            </ModulesContextProvider>
        </ProductTaggingContextProvider>
    );

    const belowFoldSections = (
        <>
            <div>
                <Suspense fallback={<ProjectSharesSkeleton />}>
                    <ProjectSharesAndTags />
                </Suspense>
                <ProjectDesktopWebsiteCTA />
                <ProjectPageContent />
            </div>

            <div className="my-10 flex flex-col gap-10 md:mb-[12.8125rem] md:mt-20 md:gap-[13.75rem]">
                <Suspense fallback={null}>
                    <PageBottomSection openLogin={openLogin} />
                </Suspense>
            </div>
        </>
    );

    return (
        <>
            <Container background="gray">
                <PickerContext
                    containerRef={modulesContainerRef}
                    projectId={project.ID}
                    projectLink={project.Link}
                    bannerUrl={project.Banner.imageUrl}
                    isDesktop={isDesktop}
                    isEnabled={false}
                >
                    <ProjectPageHeader
                        canDownloadBannerImage={canDownloadBannerImage}
                    />

                    {/* ModuleAds already handles lazy loading for deferred modules internally */}
                    {projectModules}
                    {isMobile ? (
                        <LazySection
                            rootMargin="300px"
                            fallback={<BelowFoldSkeleton />}
                        >
                            {belowFoldSections}
                        </LazySection>
                    ) : (
                        belowFoldSections
                    )}
                </PickerContext>
            </Container>
        </>
    );
};

export default ProjectPage;
